import React, { ComponentProps, useCallback } from 'react'
import { HStack, Modal } from '@musinsa/mwms-ds'
import Input from '@/components/content/Input'
import { useAddDeliveryArea } from '@/services/deliveryAreas/useDeliveryAreasQueries'
import { FieldPathValue, useForm } from 'react-hook-form'
import { AddDeliveryAreaReq } from '@/services/deliveryAreas/types'
import { debounce, isEmpty, toNumber } from 'lodash-es'
import RadioBool from '@/components/content/RadioBool'
import SelectBoxCity from '@/pages/master/deliveryAreas/components/SelectBoxCity'
import MToast from '@/utils/modal/MToast'
import MAlert from '@/utils/modal/MAlert'
import { Utils } from '@/utils/Utils'
import { DA_CITY_WITH_NULLABLE_DISTRICT } from '@/pages/master/deliveryAreas/components/consts'
import { SelectBoxCarrier } from './SelectBoxCarrier'
import { FormField } from '@/components/content/FormField'
import styled from 'styled-components'
import Text from '@/components/content/Text'
import { colorSystem } from '@/styles/theme'
import { AxiosError } from 'axios'
import { addDeliveryAreaReqSchema, checkDeliveryLeadDay, checkZipCodeDuplication } from '@/services/deliveryAreas/schemas'
import { zodResolver } from '@hookform/resolvers/zod'

interface Props {
  open: boolean
  onClose: () => void
  onRefresh: () => Promise<void>
}

const ZIPCODE_DUPLICATION_CUSTOM_ERROR = 'ZIPCODE_DUPLICATION'

const defaultValues: Partial<AddDeliveryAreaReq> = {
  zipcode: '',
  city: undefined,
  district: '',
  town: '',
  deliveryLeadDay: undefined,
  remoteArea: false,
  isHolidayDeliveryAvailable: false,
  defaultCarrier: undefined,
  holidayCarrier: undefined,
}

/**
 *  validation 정책
 *  - isValid일때만 추가 버튼이 활성화
 *  - onBlur시 validation 체크
 *  - 에러가 존재할시엔 onChange validation 체크
 */
function DeliveryAreasAddModal({ open, onClose, onRefresh }: Props) {
  const addDeliveryArea = useAddDeliveryArea()

  const {
    watch,
    setValue,
    setError,
    trigger,
    getValues,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm<AddDeliveryAreaReq>({
    resolver: zodResolver(addDeliveryAreaReqSchema),
    defaultValues,
  })

  const form = watch()
  const isValid = addDeliveryAreaReqSchema.safeParse(form).success && isEmpty(errors)

  const isZipCodeDuplicateError = errors.zipcode?.type === ZIPCODE_DUPLICATION_CUSTOM_ERROR
  const isRequiredDistrict = form.city !== DA_CITY_WITH_NULLABLE_DISTRICT

  const handleSetValue = <K extends keyof AddDeliveryAreaReq>(key: K, value: NonNullable<FieldPathValue<AddDeliveryAreaReq, K>>) => {
    // error가 존재할때는 onChange마다 체크
    setValue(key, value, { shouldValidate: !!errors[key] })
  }

  const handleZipCodeDuplication = useCallback(
    debounce(async (zipcode: string) => {
      if (!zipcode) return

      const { isValid, message } = await checkZipCodeDuplication(zipcode)

      if (!isValid) {
        setError('zipcode', { message, type: ZIPCODE_DUPLICATION_CUSTOM_ERROR })
        return
      }
      if (isZipCodeDuplicateError) {
        clearErrors('zipcode')
      }
    }, 300),
    [setError, clearErrors, isZipCodeDuplicateError],
  )

  const handleCityChange = (value: string) => {
    setValue('city', value)

    if (value === DA_CITY_WITH_NULLABLE_DISTRICT) {
      setValue('district', null, { shouldValidate: true })
      return
    }

    const { district } = getValues()
    if (district === null) {
      setValue('district', '')
    }
  }

  const handleLeadDayTrigger = () => {
    const { deliveryLeadDay, remoteArea } = getValues()

    const { isValid, message } = checkDeliveryLeadDay({ deliveryLeadDay, remoteArea })

    if (!isValid) {
      setError('deliveryLeadDay', { message, type: ZIPCODE_DUPLICATION_CUSTOM_ERROR })
      return
    }
    clearErrors('deliveryLeadDay')
  }

  const handleFormSubmit = (formData: AddDeliveryAreaReq) => {
    addDeliveryArea
      .mutateAsync({
        ...formData,
        district: formData.district,
      })
      .then(() => {
        MToast.show('등록에 성공했습니다.')

        setTimeout(() => {
          onRefresh()
          onClose()
        }, 100)
      })
      .catch((e: AxiosError<ApiResponseV2>) => {
        MAlert.show(Utils.getApiErrMessage(e, '추가에 실패했습니다.'))
      })
  }

  return !open ? null : (
    <Modal
      open
      size="md"
      title="우편번호 추가"
      buttons={[
        { kind: 'secondary', content: '취소', onClick: onClose },
        { kind: 'primary_light', content: '추가', onClick: handleSubmit(handleFormSubmit), disabled: !isValid },
      ]}
      onClose={onClose}
    >
      <div>
        <InputFormField
          label="우편번호"
          errorMessage={errors?.zipcode?.message}
          inputProps={{
            type: 'number',
            value: form.zipcode,
            onChange: (v) => {
              // isZipCodeDuplicateError 우선순위가 더 높음
              setValue('zipcode', v, { shouldValidate: !!errors.zipcode && !isZipCodeDuplicateError })
              handleZipCodeDuplication(v)
            },
            onBlur: () => {
              if (!isZipCodeDuplicateError) {
                trigger('zipcode')
              }
            },
          }}
        />
        <Flex>
          <FormField label="시구분" required>
            <SelectBoxCity value={form.city} onChange={handleCityChange} />
          </FormField>
          <InputFormField
            label="구구분"
            required={isRequiredDistrict}
            errorMessage={errors?.district?.message}
            inputProps={{
              disabled: !isRequiredDistrict,
              value: form.district || '',
              onChange: (v) => handleSetValue('district', v),
              onBlur: () => trigger('district'),
            }}
          />
        </Flex>
        <Flex>
          <InputFormField
            label="읍면동"
            errorMessage={errors?.town?.message}
            inputProps={{
              value: form.town,
              onChange: (v) => handleSetValue('town', v),
              onBlur: () => trigger('town'),
            }}
          />
          <FormField label="휴일 배송 여부" required>
            <RadioBool value={form.isHolidayDeliveryAvailable} onChange={(v) => setValue('isHolidayDeliveryAvailable', v)} />
          </FormField>
        </Flex>
        <Flex>
          <FormField label="도서산간여부" required>
            <RadioBool
              value={form.remoteArea}
              onChange={(v) => {
                setValue('remoteArea', v)
                handleLeadDayTrigger()
              }}
            />
          </FormField>
          <InputFormField
            label="배송리드타임"
            errorMessage={errors?.deliveryLeadDay?.message}
            inputProps={{
              type: 'number',
              value: form.deliveryLeadDay,
              placeholder: form.remoteArea ? '0~9 숫자만 입력해 주세요.' : '1~3 숫자만 입력해 주세요.',
              onChange: (v) => handleSetValue('deliveryLeadDay', toNumber(v)),
              onBlur: handleLeadDayTrigger,
            }}
          />
        </Flex>
        <Flex>
          <FormField label="주중 운송사" required>
            <SelectBoxCarrier value={form.defaultCarrier} onChange={(v) => setValue('defaultCarrier', v)} />
          </FormField>
          <FormField label="휴일 운송사" required>
            <SelectBoxCarrier value={form.holidayCarrier} onChange={(v) => setValue('holidayCarrier', v)} />
          </FormField>
        </Flex>
      </div>
    </Modal>
  )
}

export default DeliveryAreasAddModal

function InputFormField({
  label,
  required = true,
  errorMessage,
  inputProps,
}: {
  label: string
  required?: boolean
  errorMessage?: string
  inputProps: ComponentProps<typeof Input>
}) {
  return (
    <FormField label={label} required={required}>
      <Input {...inputProps} />
      {errorMessage && (
        <ErrorMessage kind="Body_12_medium" color={colorSystem.text.common.error}>
          {errorMessage}
        </ErrorMessage>
      )}
    </FormField>
  )
}

const ErrorMessage = styled(Text)`
  margin-top: 2px;
  margin-left: 4px;
`

const Flex = styled(HStack)`
  & > .form-field {
    border-top: none;
  }
  & > :nth-last-child(1) {
    border-left: none;
  }
`
